import { DeliveryBike, Heart, Pedestrian } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import React from 'react';
import { Field, Form } from 'react-final-form';
import styled from 'styled-components';

import FormSection from './FormSection';
import LargeCheckBox from './LargeCheckBox';
import { RFFFieldErrorWrapper } from '../../../../../common/components';
import { Body3 } from '../../../../../common/typography/Typography';
import validateYupSchema from '../../../../../common/utils/validateYupSchema';
import { CommissionsConfig } from '../../../SelfServeFormView/SelfServeSignUpForm/types';
import { CommissionsPreferences, CommissionsPreferencesSchema } from '../validation';

type CommissionsFormProps = {
  formId: string;
  commissionConfig: CommissionsConfig;
  commissionsPreferences: CommissionsPreferences;
  handleSubmit: (values: CommissionsPreferences) => Promise<void | { [key: string]: string | Error }>;
};

const FormSectionWrapper = styled.div`
  margin-block-end: 24px;
`;

const JoinWoltPlus = styled.div`
  > img {
    width: 100%;
  }
`;

const JoinWoltPlusCheckboxWrapper = styled.div`
  margin-block-start: 16px;
  margin-block-end: 8px;
`;

const SectionDivider = styled.hr`
  margin: 16px 0;
  border: none;
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const CommissionSection = styled.div``;

const IconWrapper = styled.div`
  svg {
    height: 24px;
  }

  height: 32px;
`;

const CommissionRowWrapper = styled.div`
  section {
    > div:nth-child(1) {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      margin-block-end: 4px;
    }

    > div:nth-child(2) {
      font-weight: 400;
      line-height: 20px;
      color: ${cbSemanticCssVars.textSecondary};
    }
  }
`;

type CommissionHeaderProps = {
  icon?: React.ReactNode;
  title: string;
  commission: string;
  description: string;
};

const CommissionRow = ({ icon, title, commission, description }: CommissionHeaderProps) => (
  <CommissionRowWrapper>
    {icon && <IconWrapper>{icon}</IconWrapper>}
    <section>
      <div>
        <Body3>{title}</Body3>
        <Body3>{commission}%</Body3>
      </div>
      <div>
        <Body3>{description}</Body3>
      </div>
    </section>
  </CommissionRowWrapper>
);

const CommissionsForm = ({ handleSubmit, formId, commissionConfig, commissionsPreferences }: CommissionsFormProps) => {
  return (
    <Form<CommissionsPreferences>
      initialValues={{
        joinedToWoltPlus: commissionsPreferences.joinedToWoltPlus || false,
      }}
      onSubmit={handleSubmit}
      validate={validateYupSchema(CommissionsPreferencesSchema)}
    >
      {({ handleSubmit }) => (
        <form id={formId} onSubmit={handleSubmit}>
          <FormSectionWrapper>
            <FormSection>
              {commissionConfig.trialCommission && (
                <>
                  <CommissionSection>
                    <CommissionRow
                      icon={<Heart />}
                      title={`${commissionConfig.trialCommission.commissionDuration} day trial period`}
                      commission={`${commissionConfig.trialCommission.commissionValue}`}
                      description={`Wolt won’t charge any commission for ${commissionConfig.trialCommission.commissionDuration} days.`}
                    />
                  </CommissionSection>
                  <SectionDivider />
                </>
              )}
              {commissionConfig.deliveryCommission && (
                <>
                  <CommissionSection>
                    <CommissionRow
                      icon={<DeliveryBike />}
                      title="Wolt delivery"
                      commission={`${commissionConfig.deliveryCommission}`}
                      description={`Wolt charges ${commissionConfig.deliveryCommission}% commission on each order delivered by Wolt couriers.`}
                    />
                  </CommissionSection>
                  <SectionDivider />
                </>
              )}
              {commissionConfig.takeawayCommission && (
                <CommissionSection>
                  <CommissionRow
                    icon={<Pedestrian />}
                    title="Takeaway"
                    commission={`${commissionConfig.takeawayCommission}`}
                    description={`Wolt charges ${commissionConfig.takeawayCommission}% commission on each order that customers pick-up.`}
                  />
                </CommissionSection>
              )}
            </FormSection>
          </FormSectionWrapper>
          {commissionConfig.subscriptionCommission && (
            <FormSectionWrapper>
              <FormSection title="Want to get more?">
                <JoinWoltPlus>
                  <img
                    src={`${process.env.CDN_URL}/onboarding/ssmo/service-pricing/wolt-plus.png`}
                    alt="Join Wolt+ banner"
                  />
                  <JoinWoltPlusCheckboxWrapper>
                    <Field<boolean> name="joinedToWoltPlus" type="checkbox">
                      {field => (
                        <RFFFieldErrorWrapper field={field}>
                          {input => <LargeCheckBox checked={input.checked} onChange={input.onChange} />}
                        </RFFFieldErrorWrapper>
                      )}
                    </Field>
                  </JoinWoltPlusCheckboxWrapper>
                  <CommissionRow
                    title="Join Wolt+"
                    description={`For an additional ${commissionConfig.subscriptionCommission}% commission, Wolt+ gives you access to customers who enjoy free delivery, hence order more and more frequently!`}
                    commission={`+${commissionConfig.subscriptionCommission}`}
                  />
                </JoinWoltPlus>
              </FormSection>
            </FormSectionWrapper>
          )}
        </form>
      )}
    </Form>
  );
};

export default CommissionsForm;
